import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useScopeMateriality } from '../../../hooks/analytics/useScopeReports.ts';
import { RefreshMeta, StatusChip, formatDate } from '../controller/shared.tsx';
import type { ScopeMaterialityAssessment } from '../../../request/analytics/scopeReports.ts';

const KNOWN_KEYS = new Set(['id', 'cycle_name', 'benchmark', 'status', 'updated_at']);

/** Human-friendly label for a threshold key like `overall_materiality` -> "Overall Materiality". */
function labelFor(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Formats an assessment's numeric value, tolerating either a number or a numeric string from the API. */
function formatThreshold(value: unknown): string {
  if (value == null) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(value);
}

/**
 * `scope_materiality` — materiality assessment threshold(s) + benchmark.
 * The backend surfaces whatever numeric threshold columns actually exist on
 * `materialityassessments`, so this table renders any keys beyond the
 * guaranteed `id/cycle_name/benchmark/status/updated_at` generically as
 * additional threshold columns (capped to keep the table readable).
 */
export const ScopeMaterialityTable: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useScopeMateriality();
  const rows = useMemo(() => data?.assessments ?? [], [data]);

  const extraKeys = useMemo(() => {
    const keys = new Set<string>();
    rows.forEach((row) => {
      Object.keys(row).forEach((k) => {
        if (!KNOWN_KEYS.has(k)) keys.add(k);
      });
    });
    return Array.from(keys).slice(0, 3);
  }, [rows]);

  const columns = useMemo<TableColumnDef<ScopeMaterialityAssessment>[]>(
    () => [
      {
        id: 'cycle_name',
        columnLabel: 'Cycle',
        header: () => 'Cycle',
        cell: ({ row }) => row.original.cycle_name ?? '—',
        widthPercent: 18,
      },
      ...extraKeys.map((key) => ({
        id: key,
        columnLabel: labelFor(key),
        header: () => labelFor(key),
        cell: ({ row }: { row: { original: ScopeMaterialityAssessment } }) => formatThreshold(row.original[key]),
        widthPercent: Math.floor(50 / Math.max(extraKeys.length, 1)),
      })),
      {
        id: 'benchmark',
        columnLabel: 'Benchmark',
        header: () => 'Benchmark',
        cell: ({ row }) => row.original.benchmark ?? '—',
        widthPercent: 18,
      },
      {
        id: 'status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.status} />,
        widthPercent: 14,
      },
      {
        id: 'updated_at',
        columnLabel: 'Updated',
        header: () => 'Updated',
        cell: ({ row }) => formatDate(row.original.updated_at),
        widthPercent: 14,
      },
    ],
    [extraKeys]
  );

  return (
    <AppDataTable<ScopeMaterialityAssessment>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load materiality assessments.' : null}
      emptyMessage="No materiality assessments recorded yet."
      searchable
      searchPlaceholder="Search materiality assessments..."
      paginated
      defaultPageSize={10}
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default ScopeMaterialityTable;
